"use client";

import dynamic from "next/dynamic";
import { useEffect, useState, startTransition } from "react";

const SolarSystemScene = dynamic(() => import("./solar/SolarSystemScene"), {
  ssr: false,
  loading: () => null,
});

/**
 * Client island that defers the solar-system hero until AFTER first
 * paint. Identical defer strategy to the old HeroParticles island (the
 * pattern that holds Lighthouse ~97 — see git history for the tuning
 * journey): Hero stays a server component so the logo IMG paints during
 * HTML parse; this island warms the Three.js chunk on hydration and
 * mounts the canvas at first idle (800ms ceiling), off the LCP path.
 *
 *  • Skip entirely on prefers-reduced-motion.
 *  • Skip on slow connections — decorative, not worth the bandwidth.
 *  • Warm the chunk immediately (same specifier as dynamic() →
 *    webpack dedupes → render flip is an in-memory mount).
 *  • requestIdleCallback(800ms) + startTransition gate the render.
 *
 * astronomy-engine rides inside the deferred chunk — the ephemeris math
 * itself is microseconds; it never touches the initial bundle.
 */
const SolarHero = () => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const [today, setToday] = useState<string | null>(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    const conn = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection;
    const slow = conn?.effectiveType;
    if (slow && (slow === "2g" || slow === "3g" || slow === "slow-2g")) {
      return;
    }

    void import("./solar/SolarSystemScene").catch(() => {});

    type Win = typeof window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const w = window as Win;

    const load = () => {
      startTransition(() => {
        setShouldLoad(true);
        // Local date, YYYY-MM-DD — computed client-side so the static
        // build never bakes in a stale day. en-CA gives ISO ordering.
        setToday(new Date().toLocaleDateString("en-CA"));
      });
    };

    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(load, { timeout: 800 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = setTimeout(load, 600);
    return () => clearTimeout(t);
  }, []);

  if (!shouldLoad) return null;

  return (
    <>
      <SolarSystemScene />
      {/* The claim that makes this hero a flex instead of a screensaver:
          these are the planets' real positions for today. Quiet telemetry
          voice, sitting just above the hero's bottom status bar. */}
      {today && (
        <div className="hero-anim-scroll pointer-events-none absolute bottom-14 left-0 right-0 z-30 flex flex-col items-center gap-1.5 px-4">
          <p className="text-center text-[10px] leading-relaxed text-white/40 font-[family-name:var(--font-jetbrains-mono)] tracking-[0.25em] uppercase [text-shadow:0_1px_8px_rgba(0,0,0,0.9)]">
            ephemeris · {today}
          </p>
          {/* the claim is explorable — /sys is the full fly-around */}
          <a
            href="/sys"
            className="pointer-events-auto text-[10px] uppercase tracking-[0.25em] text-emerald-300/50 font-[family-name:var(--font-jetbrains-mono)] transition-colors hover:text-emerald-200"
          >
            ↗ fly_the_system
          </a>
        </div>
      )}
    </>
  );
};

export default SolarHero;
